import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { bankBalanceApi } from '../../services/bankBalanceApi';
import { ACCOUNT_TYPE_LABELS } from '../../types/bankBalance';
import type { TrendDataPoint, TypeBreakdown } from '../../types/bankBalance';

interface OverviewTabProps {
    companyCode: string | null;
    isAdmin: boolean;
}

const CURRENCY_COLORS: Record<string, string> = {
    EUR: '#3b82f6',
    CHF: '#ef4444',
    USD: '#22c55e',
    GBP: '#a855f7',
    TRY: '#f97316',
};

const TYPE_COLORS: Record<string, string> = {
    Account: '#3b82f6',
    CallDeposit: '#22c55e',
    TimeDeposit: '#a855f7',
    FixedTermLoan: '#f97316',
    CreditLine: '#06b6d4',
    IntercompanyLoan: '#6b7280',
    Unclassified: '#d1d5db',
};

const COMPANIES = ['bin', 'dbi', 'dch', 'dfr', 'dit', 'dpt', 'dsp', 'dtr'];

function formatAmount(value: number): string {
    if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + 'K';
    return value.toFixed(0);
}

function monthLabel(year: number, month: number): string {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
}

export function OverviewTab({ companyCode, isAdmin }: OverviewTabProps) {
    const [trend, setTrend] = useState<TrendDataPoint[]>([]);
    const [byType, setByType] = useState<TypeBreakdown[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterCompany, setFilterCompany] = useState(companyCode || '');

    const fetchTrend = useCallback(async () => {
        try {
            setLoading(true);
            const data = await bankBalanceApi.getTrend(filterCompany || undefined);
            setTrend(data.trend);
            setByType(data.byType);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
        }
    }, [filterCompany]);

    useEffect(() => { fetchTrend(); }, [fetchTrend]);

    // Transform trend data into chart format: [{month: 'Jan 26', EUR: 1234, CHF: 456}, ...]
    const barChartData = useMemo(() => {
        const monthMap = new Map<string, Record<string, unknown>>();

        for (const d of trend) {
            const key = `${d.PostingYear}-${String(d.PostingMonth).padStart(2, '0')}`;
            if (!monthMap.has(key)) {
                monthMap.set(key, { month: monthLabel(d.PostingYear, d.PostingMonth), sortKey: key });
            }
            const row = monthMap.get(key)!;
            row[d.CurrencyCode] = d.TotalAmount;
        }

        return Array.from(monthMap.values())
            .sort((a, b) => (a.sortKey as string).localeCompare(b.sortKey as string))
            .slice(-12);
    }, [trend]);

    const currencies = useMemo(() => {
        const set = new Set(trend.map(d => d.CurrencyCode));
        return Array.from(set).sort();
    }, [trend]);

    // Donut chart: aggregate by AccountType (sum across currencies)
    const donutData = useMemo(() => {
        const typeMap = new Map<string, number>();
        for (const d of byType) {
            const current = typeMap.get(d.AccountType) || 0;
            typeMap.set(d.AccountType, current + Math.abs(d.TotalAmount));
        }
        return Array.from(typeMap.entries())
            .map(([name, value]) => ({ name: ACCOUNT_TYPE_LABELS[name] || name, value, key: name }))
            .filter(d => d.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [byType]);

    // Line chart: trend per currency over time
    const lineChartData = useMemo(() => {
        const monthMap = new Map<string, Record<string, unknown>>();
        for (const d of trend) {
            const key = `${d.PostingYear}-${String(d.PostingMonth).padStart(2, '0')}`;
            if (!monthMap.has(key)) {
                monthMap.set(key, { month: monthLabel(d.PostingYear, d.PostingMonth), sortKey: key });
            }
            monthMap.get(key)![d.CurrencyCode] = d.TotalAmount;
        }
        return Array.from(monthMap.values())
            .sort((a, b) => (a.sortKey as string).localeCompare(b.sortKey as string))
            .slice(-12);
    }, [trend]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }

    if (trend.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium">No data available</p>
                <p className="text-sm mt-1">Enter bank balances to see the overview charts.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Company filter */}
            {isAdmin && (
                <div className="relative inline-block">
                    <select
                        value={filterCompany}
                        onChange={e => setFilterCompany(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {COMPANIES.map(c => (
                            <option key={c} value={c}>{c.toUpperCase()}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            )}

            {/* Top row: Stacked Bar + Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Stacked Bar — Cash Position by Month */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Cash Position by Month</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={barChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={formatAmount} tick={{ fontSize: 11 }} width={60} />
                            <Tooltip
                                formatter={(value) => formatAmount(value as number)}
                                labelStyle={{ fontWeight: 600 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {currencies.map(ccy => (
                                <Bar
                                    key={ccy}
                                    dataKey={ccy}
                                    stackId="cash"
                                    fill={CURRENCY_COLORS[ccy] || '#94a3b8'}
                                    radius={currencies.indexOf(ccy) === currencies.length - 1 ? [3, 3, 0, 0] : undefined}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Donut — by Account Type */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribution by Type</h3>
                    {donutData.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={donutData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {donutData.map((entry) => (
                                            <Cell key={entry.key} fill={TYPE_COLORS[entry.key] || '#94a3b8'} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatAmount(value as number)} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center">
                                {donutData.map(d => (
                                    <div key={d.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[d.key] || '#94a3b8' }} />
                                        {d.name}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-gray-400 text-center py-8">No type data</p>
                    )}
                </div>
            </div>

            {/* Bottom: Trend Lines per Currency */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Trend by Currency (12 Months)</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={lineChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={formatAmount} tick={{ fontSize: 11 }} width={60} />
                        <Tooltip
                            formatter={(value) => formatAmount(value as number)}
                            labelStyle={{ fontWeight: 600 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {currencies.map(ccy => (
                            <Line
                                key={ccy}
                                type="monotone"
                                dataKey={ccy}
                                stroke={CURRENCY_COLORS[ccy] || '#94a3b8'}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
